import { useState, useRef, useEffect } from "react";
import { ExpandMore } from "@mui/icons-material";
import { palette } from "../../themes/theme";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import * as fabric from "fabric";

import {
  Box,
  styled,
  Button,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Slider,
  FormControlLabel,
  Checkbox,
  debounce,
} from "@mui/material";
import logo from "../../assets/images/CrimeRisk.AI Final Logo.png";
import ColorPicker from "../../component/ColorPicker";
import {
  MapStyleWrapper,
  ScrollContainer,
  StyledSwitch,
  StyledTextField,
} from "./StyledComps";
import CustomNumberInput from "../PrimaryNumberField";
import { postImageSearch } from "../../api";
import { InsertImageTabs } from "./InsertImageTabs";
import { off } from "cropperjs";

const fontStyles = [
  { value: "normal", key: "Regular" },
  { value: "bold", key: "Bold" },
  { value: "italic", key: "Italic" },
  // { value: "boldItalic", key: "Bold & Italic" },
];
const LogoImage = styled("img")(() => ({
  marginBottom: "24px",
  width: "280px",
}));

const fontFamilies = [
  { value: "Arial", key: "Arial" },
  { value: "Verdana", key: "Verdana" },
  { value: "Helvetica", key: "Helvetica" },
  { value: "Times New Roman", key: "Times New Roman" },
  { value: "Courier New", key: "Courier New" },
  { value: "monospace", key: "Monospace" },
  { value: "Roboto", key: "Roboto" },
];

const frameStyle = [
  { name: "Classic", img: "./customFrames/classic.png" },
  { name: "Label", img: "./customFrames/label.png" },
  { name: "Insta", img: "./customFrames/insta.png" },
  { name: "Clean", img: "./customFrames/clean.png" },
  { name: "Bold", img: "./customFrames/bold.png" },
  { name: "Elegant", img: "./customFrames/elegant.png" },
  { name: "Circle", img: "./customFrames/circle.png" },
];

const greenButtonStyle = {
  flex: 1,
  color: "black",
  bgcolor: "white",
  border: "2px solid #7ED957",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: "500",
  ":hover": {
    bgcolor: "#f0f9ff",
    borderColor: "#7ED957",
  },
};

const actionButtonStyle = {
  flex: 1,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: "500",
};

const accordionStyle = {
  bgcolor: "white",
  border: "2px solid #7ED957",
  borderRadius: "8px !important",
  boxShadow: "none",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "4px 0 !important",
  },
};

const accordionSummaryStyle = {
  bgcolor: "white",
  borderRadius: "8px",
  minHeight: "48px",
  "&.Mui-expanded": {
    minHeight: "48px",
    borderBottom: "1px solid #e0e0e0",
    borderRadius: "8px 8px 0 0",
  },
  "& .MuiAccordionSummary-content": {
    margin: "12px 0",
    "&.Mui-expanded": {
      margin: "12px 0",
    },
  },
};

export default function Sidebar({
  activeLogo,
  uploadedLogos,
  setUploadedLogos,
  setPreview,
  preview,
  updateSelectedObjectScale,
  setSelectedObjectScale,
  selectedObjectScale,
  exportFormat,
  setExportFormat,
  leagendUrl = "",
  toggleImageVisibilityByKey,
  isLeagend,
  setIsLeagend,
  toggleBackgroundInteraction,
  addImageToCanvas,
  setFrameText,
  frameText,
  addText,
  setFrameDesign,
  setIsLoading,
  frameDesign,
  setShowShapeSelector,
  downloadImage,
  deleteActiveObject,
  bringForward,
  sendBackward,
  bringToFront,
  sendToBack,
  zoomIn,
  zoomOut,
  resetZoom,
  showShapeSelector,
  addSVGFromString,
  openSVGPicker,
  svgInputRef,
  handleSVGFile,
  shapes,
  addCustomShape,
  selectedTextProps,
  setSelectedTextProps,
  updateActiveTextProperty,
  selectedShapeProps,
  setSelectedShapeProps,
  updateActiveShapeProperty,
}) {
  const [textAccordionExpanded, setTextAccordionExpanded] = useState(false);
  const [shapeAccordionExpanded, setShapeAccordionExpanded] = useState(false);
  const [frameAccordionExpanded, setFrameAccordionExpanded] = useState(false);
  const [imageAccordionExpended, setImageAccordionExpended] = useState(false);
  const [isInteractable, setInteractable] = useState(false);
  const [logoOptions, setLogoOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const canvasRef = useRef(null);
  const fabricCanvas = useRef(null);

  const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
    color: palette.text.dark,
  }));

  const changeStyle = (key, value) => {
    switch (key) {
      case "textBoxDropShadow-transparent":
        value = value.target.checked;
        key = "isDropShadowTransparent";
        break;
      case "textBoxFillColor-transparent":
        value = value.target.checked;
        key = "textBoxFillColorTransparent";
        break;
      case "textBoxBorderColor-transparent":
        value = value.target.checked;
        key = "textBoxBorderColorTransparent";
        break;
      case "leaderLineColor-transparent":
        value = value.target.checked;
        key = "isLineTransparent";
        break;
      case "transparency":
        value = value;
        key = "textBoxOpacity";

        break;
      default:
        break;
    }

    const tempStyle = {
      ...selectedTextProps,
      [key]: value,
    };
    setSelectedTextProps(tempStyle);
  };

  const handleUploadPreviewSVG = async () => {
    if (!preview) {
      console.warn("No preview URL set.");
      return;
    }

    try {
      const response = await fetch(preview);

      if (!response.ok) {
        throw new Error(`Failed to fetch SVG from: ${preview}`);
      }

      const svgText = await response.text();
      await addSVGFromString(svgText);
    } catch (err) {
      console.error("Error uploading SVG from preview URL:", err);
    }
  };

  const searchLogo = debounce(async (e) => {
    setLoading(true);
    console.log("search");
    const value = e.target.value.trim();
    if (!value) {
      setLoading(false);
      return;
    }
    const res = await postImageSearch({
      body: { value },
    });
    setLoading(false);
    if (!res?.data) return;
    setLogoOptions(
      res.data.poiImages.map((d) => ({
        label: d.poiName,
        retailerId: d.retailerId,
      }))
    );
  }, 1000);

  useEffect(() => {
    // Init Fabric Canvas only once
    if (fabricCanvas.current) {
      fabricCanvas.current.dispose();
    }

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 800,
      height: 950,
      backgroundColor: "#f3f3f3",
    });

    fabricCanvas.current = canvas;

    return () => {
      canvas.dispose();
    };
  }, []);

  return (
    <Box
      sx={{
        width: 425,
        height: "100vh",
        bgcolor: "#f8f9fa",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        overflowY: "auto",
        borderRight: "1px solid #e0e0e0",
      }}
    >
      <Stack alignItems="center">
        <LogoImage alt="crimeRisk Logo" src={logo} />
      </Stack>

      {/* Action Section */}
      <Box>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{ mb: 2, color: "#333", fontSize: "16px" }}
        >
          Action
        </Typography>
        <Stack direction={"column"} spacing={2} marginBottom={3}>
          <Button
            variant="contained"
            onClick={deleteActiveObject}
            sx={{
              ...actionButtonStyle,
              color: "white",
              bgcolor: "#dc3545",
              ":hover": { bgcolor: "#c82333" },
            }}
          >
            DELETE
          </Button>
        </Stack>
        <Stack direction="column" spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Format</InputLabel>
            <Select
              sx={{ color: palette.text.dark }}
              value={exportFormat}
              label="Format"
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <CustomMenuItem value={"png"}>PNG</CustomMenuItem>
              <CustomMenuItem value={"jpeg"}>JPEG</CustomMenuItem>
              <CustomMenuItem value={"pdf"}>PDF</CustomMenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => {
              setIsLoading(true);
              downloadImage(exportFormat);
            }}
            sx={{
              ...actionButtonStyle,
              color: "white",
              bgcolor: "#7ED957",
              ":hover": { bgcolor: "#6bc946" },
            }}
          >
            DOWNLOAD
          </Button>
        </Stack>

        <Stack direction={"row"} marginTop={2} spacing={8}>
          <Stack spacing={2}>
            <Typography
              variant="46"
              fontWeight="400"
              sx={{ mb: 2, color: "#333", fontSize: "16px" }}
            >
              Map Movable
            </Typography>
            <FormControlLabel
              control={
                <StyledSwitch
                  checked={isInteractable}
                  onChange={(e) => {
                    toggleBackgroundInteraction(!isInteractable);
                    setInteractable(e.target.checked);
                  }}
                />
              }
            />
          </Stack>
          {leagendUrl && (
            <Stack spacing={2}>
              <Typography
                variant="46"
                fontWeight="400"
                sx={{ mb: 2, color: "#333", fontSize: "16px" }}
              >
                Legend
              </Typography>
              <FormControlLabel
                control={
                  <StyledSwitch
                    checked={isLeagend}
                    onChange={(e) => {
                      toggleImageVisibilityByKey(
                        "legendImage",
                        e.target.checked
                      );
                      setIsLeagend(e.target.checked);
                    }}
                  />
                }
                label="Leagend"
              />
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Insert Section with Accordions */}
      <Box>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{ mb: 2, color: "#333", fontSize: "16px" }}
        >
          Insert
        </Typography>

        <Stack spacing={1}>
          {/* Text Accordion */}
          <Accordion
            expanded={textAccordionExpanded}
            onChange={() => setTextAccordionExpanded(!textAccordionExpanded)}
            sx={accordionStyle}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "#7ED957" }} />}
              sx={accordionSummaryStyle}
            >
              <Typography sx={{ fontWeight: "500", color: "#333" }}>
                Text
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2, pb: 2 }}>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  onClick={addText}
                  sx={{
                    bgcolor: "#7ED957",
                    color: "white",
                    borderRadius: "8px",
                    marginTop: "6px",
                    textTransform: "none",
                    fontWeight: "500",
                    ":hover": { bgcolor: "#6bc946" },
                  }}
                >
                  Add Text
                </Button>

                {selectedTextProps && (
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="600"
                      sx={{ color: "#333" }}
                    >
                      Text Properties
                    </Typography>
                    {/* Color and Font Family */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ minWidth: "60px", color: "#333" }}>
                        Color:
                      </Typography>
                      <ColorPicker
                        //   checked={polygonStyleRef.current.polygonColorTransparent}
                        color={selectedTextProps.fill}
                        noCheckbox
                        setColor={(e, color) => {
                          setSelectedTextProps({
                            ...selectedTextProps,
                            fill: color,
                          });
                          updateActiveTextProperty("fill", color);
                        }}
                        type={"textBoxFontColor"}
                      />
                      <FormControl fullWidth>
                        <InputLabel sx={{ top: "-10px" }}>
                          Font family
                        </InputLabel>
                        <Select
                          sx={{
                            color: palette.text.dark,
                            py: 0,
                            "& .MuiSelect-outlined": { py: "5px" },
                          }}
                          value={selectedTextProps?.fontFamily || "Arial"}
                          label="fontFamily"
                          onChange={(e) => {
                            updateActiveTextProperty(
                              "fontFamily",
                              e.target.value
                            );
                            setSelectedTextProps({
                              ...selectedTextProps,
                              fontFamily: e.target.value,
                            });
                          }}
                        >
                          {fontFamilies.map((d, i) => (
                            <CustomMenuItem key={i} value={d.value}>
                              {d.key}
                            </CustomMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    {/* Font Size and Style */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ minWidth: "60px", color: "#333" }}>
                        Size:
                      </Typography>

                      <CustomNumberInput
                        value={selectedTextProps.fontSize}
                        onChange={(e) => {
                          setSelectedTextProps({
                            ...selectedTextProps,
                            fontSize: e,
                          });
                          updateActiveTextProperty("fontSize", e);
                        }}
                        min={14}
                        max={100}
                      />
                      <FormControl fullWidth sx={{ width: "110px", pl: "4px" }}>
                        <InputLabel sx={{ top: "-10px" }}>
                          Font style
                        </InputLabel>

                        <Select
                          sx={{
                            color: palette.text.dark,
                            py: 0,
                            "& .MuiSelect-outlined": { py: "5px" },
                          }}
                          value={selectedTextProps.fontWeight}
                          label="fontStyle"
                          onChange={(e) => {
                            setSelectedTextProps({
                              ...selectedTextProps,
                              fontWeight: e.target.value,
                            });
                            updateActiveTextProperty(
                              "fontWeight",
                              e.target.value
                            );
                          }}
                        >
                          {fontStyles.map((d, i) => (
                            <CustomMenuItem key={i} value={d.value}>
                              {d.key}
                            </CustomMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    {/* Text Box Shadow */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              sx={{ p: 0 }}
                              checked={
                                !!selectedTextProps.isDropShadowTransparent
                              }
                              onChange={(e) => {
                                changeStyle(`textBoxDropShadow-transparent`, e);
                                setSelectedTextProps((prev) => ({
                                  ...prev,
                                  isDropShadowTransparent: e.target.checked
                                    ? "rgba(0,0,0,0.5)"
                                    : null,
                                }));
                                updateActiveTextProperty("shadow", {
                                  color: e.target.checked
                                    ? "rgba(0,0,0,0.5)"
                                    : "rgba(0,0,0,0)",
                                  blur: selectedTextProps?.blur || 3,
                                  offsetX: selectedTextProps?.offsetX || 10,
                                  offsetY: selectedTextProps?.offsetY || 10,
                                });
                              }}
                            />
                          }
                          sx={{ m: 0 }}
                        />

                        <Typography
                          sx={{
                            color: palette.text.dark,
                            fontSize: 15,
                          }}
                        >
                          Text Shadow
                        </Typography>
                      </Box>
                    </Stack>

                    {/* offsetX: 10, */}
                    <Stack width={"100%"} spacing={1}>
                      <Typography
                        sx={{ color: palette.text.dark, fontSize: 15 }}
                      >
                        X Offset:
                      </Typography>
                      <Slider
                        min={1}
                        step={1}
                        max={50}
                        value={selectedTextProps?.offsetX || 10}
                        valueLabelDisplay="auto"
                        onChange={(key, value) => {
                          // changeStyle("transparency", value);
                          setSelectedTextProps((prev) => ({
                            ...prev,
                            offsetX: value,
                          }));

                          updateActiveTextProperty("shadow", {
                            color: selectedTextProps.isDropShadowTransparent
                              ? "rgba(0,0,0,0.5)"
                              : "rgba(0,0,0,0)",
                            blur: 3,
                            offsetX: value,
                            offsetY: selectedTextProps?.offsetY || 10,
                          });
                        }}
                      />
                    </Stack>

                    <Stack width={"100%"} spacing={1}>
                      <Typography
                        sx={{ color: palette.text.dark, fontSize: 15 }}
                      >
                        Y Offset:
                      </Typography>
                      <Slider
                        min={1}
                        step={1}
                        max={50}
                        value={selectedTextProps?.offsetY || 10}
                        valueLabelDisplay="auto"
                        onChange={(key, value) => {
                          // changeStyle("transparency", value);
                          setSelectedTextProps((prev) => ({
                            ...prev,
                            offsetY: value,
                          }));

                          updateActiveTextProperty("shadow", {
                            color: selectedTextProps.isDropShadowTransparent
                              ? "rgba(0,0,0,0.5)"
                              : "rgba(0,0,0,0)",
                            blur: 3,
                            offsetY: value,
                            offsetX: selectedTextProps?.offsetX || 10,
                          });
                        }}
                      />
                    </Stack>

                    <Stack width={"100%"} spacing={1}>
                      <Typography
                        sx={{ color: palette.text.dark, fontSize: 15 }}
                      >
                        Blur
                      </Typography>
                      <Slider
                        min={1}
                        step={1}
                        max={50}
                        value={selectedTextProps?.blur || 10}
                        valueLabelDisplay="auto"
                        onChange={(key, value) => {
                          // changeStyle("transparency", value);
                          setSelectedTextProps((prev) => ({
                            ...prev,
                            blur: value,
                          }));

                          updateActiveTextProperty("shadow", {
                            color: selectedTextProps.isDropShadowTransparent
                              ? "rgba(0,0,0,0.5)"
                              : "rgba(0,0,0,0)",
                            blur: value,
                            offsetY: selectedTextProps?.offsetY || 10,
                            offsetX: selectedTextProps?.offsetX || 10,
                          });
                        }}
                      />
                    </Stack>

                    {/* Text Box Background Color and Transparency */}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Image Accordion */}
          <Accordion
            expanded={imageAccordionExpended}
            onChange={() => setImageAccordionExpended(!imageAccordionExpended)}
            sx={accordionStyle}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "#7ED957" }} />}
              sx={accordionSummaryStyle}
            >
              <Typography sx={{ fontWeight: "500", color: "#333" }}>
                Add Image
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2, pb: 2 }}>
              {/* select image Stack */}
              <InsertImageTabs
                activeLogo={activeLogo}
                uploadedLogos={uploadedLogos}
                setUploadedLogos={setUploadedLogos}
                handleUploadPreviewSVG={handleUploadPreviewSVG}
                searchLogo={searchLogo}
                logoOptions={logoOptions}
                loading={loading}
                preview={preview}
                setPreview={setPreview}
                addImageToCanvas={addImageToCanvas}
              />
            </AccordionDetails>
          </Accordion>

          {/* Shape Accordion */}
          <Accordion
            expanded={shapeAccordionExpanded}
            onChange={() => setShapeAccordionExpanded(!shapeAccordionExpanded)}
            sx={accordionStyle}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "#7ED957" }} />}
              sx={accordionSummaryStyle}
            >
              <Typography sx={{ fontWeight: "500", color: "#333" }}>
                Shapes
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2, pb: 2 }}>
              <Stack spacing={2}>
                {/* <Button
                    variant="outlined"
                    onClick={() => setShowShapeSelector(!showShapeSelector)}
                    sx={greenButtonStyle}
                  >
                    Add Shape
                  </Button> */}

                <Button
                  variant="contained"
                  onClick={openSVGPicker}
                  sx={{
                    bgcolor: "#7ED957",
                    color: "white",
                    borderRadius: "8px",
                    marginTop: "6px",
                    textTransform: "none",
                    fontWeight: "500",
                    ":hover": { bgcolor: "#6bc946" },
                  }}
                >
                  Upload SVG
                </Button>

                <input
                  ref={svgInputRef}
                  type="file"
                  accept=".svg,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={handleSVGFile}
                />

                {/* {showShapeSelector && ( */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    backgroundColor: "#f5f5f5",
                    maxHeight: 160,
                    overflowY: "auto",
                    border: "2px solid #7ED957",
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  {Object.keys(shapes).map((d, i) => (
                    <Box
                      key={i}
                      onClick={() => addCustomShape(d)}
                      sx={{
                        cursor: "pointer",
                        border: "2px solid #ddd",
                        padding: "8px",
                        borderRadius: 2,
                        bgcolor: "white",
                        "& svg": { width: 32, height: 32 },
                        "&:hover": {
                          borderColor: "#7ED957",
                          bgcolor: "#f0f9ff",
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: shapes[d].svg }}
                    />
                  ))}
                </Box>
                {/* )} */}

                {selectedShapeProps && (
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="600"
                      sx={{ color: "#333" }}
                    >
                      Shape Properties
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ minWidth: "60px", color: "#333" }}>
                        Fill:
                      </Typography>
                      <ColorPicker
                        //   checked={polygonStyleRef.current.polygonColorTransparent}
                        color={selectedShapeProps.fill}
                        noCheckbox
                        setColor={(e, color) => {
                          setSelectedShapeProps({
                            ...selectedShapeProps,
                            fill: color,
                          });
                          updateActiveShapeProperty("fill", color);
                        }}
                        type={"shapeColor"}
                      />
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ minWidth: "60px", color: "#333" }}>
                        Stroke:
                      </Typography>
                      <ColorPicker
                        //   checked={polygonStyleRef.current.polygonColorTransparent}
                        color={selectedShapeProps.stroke}
                        noCheckbox
                        setColor={(e, color) => {
                          setSelectedShapeProps({
                            ...selectedShapeProps,
                            stroke: color,
                          });
                          updateActiveShapeProperty("stroke", color);
                        }}
                        type={"shapeColor"}
                      />
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ minWidth: "60px", color: "#333" }}>
                        Stroke Width:
                      </Typography>

                      <CustomNumberInput
                        value={selectedShapeProps.strokeWidth}
                        onChange={(e) => {
                          setSelectedShapeProps({
                            ...selectedShapeProps,
                            strokeWidth: e,
                          });
                          updateActiveShapeProperty("strokeWidth", e);
                        }}
                        min={10}
                        max={50}
                      />
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              sx={{ p: 0 }}
                              checked={
                                !!selectedShapeProps.isDropShadowTransparent
                              }
                              onChange={(e) => {
                                setSelectedShapeProps((prev) => ({
                                  ...prev,
                                  isDropShadowTransparent: e.target.checked
                                    ? "rgba(0,0,0,0.5)"
                                    : null,
                                }));
                                updateActiveShapeProperty("shadow", {
                                  color: e.target.checked
                                    ? "rgba(0,0,0,0.5)"
                                    : "rgba(0,0,0,0)", // Shadow color
                                  blur: 5, // Blur radius
                                  offsetX: 20, // Horizontal offset
                                  offsetY: 20, // Vertical offset
                                });
                              }}
                            />
                          }
                          sx={{ m: 0 }}
                        />
                        <Typography
                          sx={{
                            color: palette.text.dark,
                            fontSize: 15,
                          }}
                        >
                          Shape Shadow
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack width={"100%"} alignItems="center" spacing={2}>
                      <Typography
                        sx={{ color: palette.text.dark, fontSize: 15 }}
                      >
                        Opacity:
                      </Typography>
                      <Slider
                        min={0}
                        step={1}
                        max={100}
                        value={selectedShapeProps.opacity}
                        valueLabelDisplay="auto"
                        onChange={(key, value) => {
                          // changeStyle("transparency", value);
                          setSelectedShapeProps((prev) => ({
                            ...prev,
                            opacity: value,
                          }));

                          updateActiveShapeProperty("opacity", value / 100);
                        }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
          {/* Frames Accordion */}
          <Accordion
            expanded={frameAccordionExpanded}
            onChange={() => setFrameAccordionExpanded(!frameAccordionExpanded)}
            sx={accordionStyle}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "#7ED957" }} />}
              sx={accordionSummaryStyle}
            >
              <Typography sx={{ fontWeight: "500", color: "#333" }}>
                Choose Frame
              </Typography>
            </AccordionSummary>

            {/* Custom frame Selector */}
            <ScrollContainer
              variant="scrollable"
              value={0}
              scrollButtons
              allowScrollButtonsMobile
            >
              <MapStyleWrapper
                key={"x"}
                elevation={frameDesign.frameStyle === "" ? 12 : 2}
                onClick={() => {
                  setFrameDesign((prev) => ({
                    ...prev,
                    frameStyle: "",
                  }));
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DoNotDisturbIcon sx={{ fontSize: 40, color: "red" }} />
                </Box>
              </MapStyleWrapper>

              {frameStyle.map((data, k) => (
                <MapStyleWrapper
                  key={k}
                  elevation={data.name === frameDesign.frameStyle ? 12 : 2}
                  onClick={() => {
                    setFrameDesign((prev) => ({
                      ...prev,
                      frameStyle: data.name,
                    }));
                  }}
                >
                  <img src={data.img} alt={data.name} />
                  <Typography>{data.name}</Typography>
                </MapStyleWrapper>
              ))}
            </ScrollContainer>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              gap={2}
              margin={4}
            >
              <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
                Select Color:
              </Typography>
              <ColorPicker
                color={frameDesign.frameColor || "#000000"}
                noCheckbox
                setColor={(e, color) => {
                  setFrameDesign((prev) => ({
                    ...prev,
                    frameColor: color,
                  }));
                }}
                type={"shapeColor"}
              />
            </Stack>
            <Stack
              direction="column"
              alignItems="center"
              spacing={2}
              gap={2}
              margin={4}
            >
              <StyledTextField
                label="Title"
                multiline
                maxRows={2}
                fullWidth
                value={frameText.title}
                onChange={(e) =>
                  setFrameText({
                    ...frameText,
                    title: e.target.value,
                  })
                }
              />

              <StyledTextField
                label="Sub Title"
                multiline
                maxRows={2}
                fullWidth
                value={frameText.subTitle}
                onChange={(e) =>
                  setFrameText({
                    ...frameText,
                    subTitle: e.target.value,
                  })
                }
              />
            </Stack>
          </Accordion>
        </Stack>
      </Box>

      {/* Sizing Section */}
      {/* {selectedObjectScale?.scale && (
        <Box>
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{ mb: 2, color: "#333", fontSize: "16px" }}
          >
            Sizing
          </Typography>

          <Stack spacing={2} marginBottom={3}>
            <Stack width={"100%"} alignItems="center" spacing={2}>
              <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
            
                Scale:
              </Typography>
              <Slider
                min={1}
                max={100}
                step={1}
                value={Math.round(selectedObjectScale?.scale * 100)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                onChange={(event, value) => {
                  const scaleDecimal = value / 100;

                  setSelectedObjectScale((prev) => ({
                    ...prev,
                    scale: scaleDecimal,
                  }));

                  updateSelectedObjectScale("scale", scaleDecimal);
                }}
              />
            </Stack>
            <Stack direction="row" spacing={8}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ minWidth: "40px", color: "#333" }}>
                  Height:
                </Typography>

                <CustomNumberInput
                  value={Math.round(selectedObjectScale.scaleY * 100)}
                  onChange={(e) => {
                    setSelectedObjectScale((prev) => ({
                      ...prev,
                      scaleY: Math.round(e) / 100,
                    }));
                    updateSelectedObjectScale("scaleY", e / 100);
                  }}
                  min={0}
                  max={100}
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ minWidth: "40px", color: "#333" }}>
                  Width:
                </Typography>

                <CustomNumberInput
                  value={Math.round(selectedObjectScale.scaleX * 100)}
                  onChange={(e) => {
                    setSelectedObjectScale((prev) => ({
                      ...prev,
                      scaleX: Math.round(e) / 100,
                    }));
                    updateSelectedObjectScale("scaleX", e / 100);
                  }}
                  min={0}
                  max={100}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )} */}

      {/* Position Section */}

      <Box>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{ mb: 2, color: "#333", fontSize: "16px" }}
        >
          Position
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={bringForward}
              variant="outlined"
              sx={greenButtonStyle}
            >
              BRING FORWARD
            </Button>
            <Button
              onClick={sendBackward}
              variant="outlined"
              sx={greenButtonStyle}
            >
              SEND BACKWARD
            </Button>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={bringToFront}
              variant="outlined"
              sx={greenButtonStyle}
            >
              BRING TO FRONT
            </Button>
            {/* <Button
              onClick={sendToBack}
              variant="outlined"
              sx={greenButtonStyle}
            >
              SEND TO BACK
            </Button> */}
          </Stack>
        </Stack>
      </Box>

      {/* Zoom Section */}
      <Box>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{ mb: 2, color: "#333", fontSize: "16px" }}
        >
          Zoom
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <Button onClick={zoomIn} variant="outlined" sx={greenButtonStyle}>
              ZOOM IN
            </Button>
            <Button onClick={zoomOut} variant="outlined" sx={greenButtonStyle}>
              ZOOM OUT
            </Button>
          </Stack>
          <Button onClick={resetZoom} variant="outlined" sx={greenButtonStyle}>
            RESET ZOOM
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
